import React, { useContext, useEffect, useState } from 'react';
import {
    Form, Input, Button, message, Row, Col, Breadcrumb, Select, Card, Avatar, Grid, Tooltip, Space, Divider
} from 'antd';
import {
    RightOutlined, UserOutlined, PhoneOutlined, MailOutlined, HomeOutlined,
    GlobalOutlined, LockOutlined, SaveOutlined, EditOutlined, SafetyOutlined,
    CheckCircleOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { UserService } from '../../common/services';
import { AuthContext } from '../../common/constants';
import { toast } from 'react-toastify';

const countryFlags = {
    civ: '🇨🇮',
    fr: '🇫🇷',
    us: '🇺🇸',
    uk: '🇬🇧'
};

const getAvatarColor = (name) => {
    const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#eb2f96', '#52c41a'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (firstName, lastName) => {
    const f = (firstName || '').charAt(0).toUpperCase();
    const l = (lastName || '').charAt(0).toUpperCase();
    return f + l || 'U';
};

const Profile = () => {
    const [form] = Form.useForm();
    const { currentUser } = useContext(AuthContext);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';

    const fullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();

    useEffect(() => {
        if (currentUser) {
            form.setFieldsValue({
                firstName: currentUser.firstName,
                lastName: currentUser.lastName,
                phone: currentUser.phone?.replace('+225', '') || '',
                email: currentUser.email,
                residence: currentUser.residence,
                country: currentUser.country || 'civ'
            });
        }
    }, [currentUser, form]);

    const handleSubmit = async (values) => {
        if (values.password && values.password !== values.confirmPassword) {
            return message.error("Les mots de passe ne correspondent pas");
        }

        const finalValues = {
            ...values,
            phone: `+225${values.phone}`
        };

        try {
            setIsSubmitting(true);
            await UserService.miseAjourProfileAdmin(currentUser._id, finalValues);
            message.success('Profil mis à jour avec succès');
        } catch (error) {
            const errorMessage = error.message || 'Une erreur est survenue lors de la mise à jour.';
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 5000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="content-wrapper">
            <style>
                {`
                    .profile-card {
                        border-radius: 12px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    }
                    .profile-card .ant-form-item-label > label {
                        font-weight: 500;
                        color: #262626;
                    }
                    .profile-card .ant-input,
                    .profile-card .ant-select-selector,
                    .profile-card .ant-input-password {
                        border-radius: 8px !important;
                    }
                    .profile-card .ant-input-group-addon {
                        border-radius: 8px 0 0 8px !important;
                        background: #fafafa;
                    }
                    .profile-card .ant-input-group .ant-input {
                        border-radius: 0 8px 8px 0 !important;
                    }
                `}
            </style>

            <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
                {/* Breadcrumb */}
                <Breadcrumb
                    separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
                    style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
                >
                    <Breadcrumb.Item>
                        <Link to="/dashboard" style={{ color: '#1890ff' }}>Accueil</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <span style={{ color: '#595959' }}>Mon Profil</span>
                    </Breadcrumb.Item>
                </Breadcrumb>

                {/* Formulaire */}
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    validateTrigger={['onChange', 'onBlur']}
                    size={controlSize}
                >
                    <Row gutter={[24, 16]}>
                        {/* Colonne gauche - Informations personnelles */}
                        <Col xs={24} lg={12}>
                            <Card
                                className="profile-card"
                                title={
                                    <span>
                                        <UserOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                                        Informations personnelles
                                    </span>
                                }
                                style={{ height: '100%' }}
                                headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            >
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="firstName"
                                            label="Nom"
                                            rules={[{ required: true, message: 'Veuillez entrer le nom' }]}
                                            normalize={v => v?.toUpperCase()}
                                        >
                                            <Input
                                                placeholder="Ex : KOUADIO"
                                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                                style={{ borderRadius: 8 }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="lastName"
                                            label="Prénoms"
                                            rules={[{ required: true, message: 'Veuillez entrer le prénom' }]}
                                            normalize={v => v?.toUpperCase()}
                                        >
                                            <Input
                                                placeholder="Ex : JEAN MICHEL"
                                                prefix={<EditOutlined style={{ color: '#bfbfbf' }} />}
                                                style={{ borderRadius: 8 }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    name="phone"
                                    label="Téléphone"
                                    rules={[
                                        { required: true, message: 'Veuillez entrer le numéro de téléphone' },
                                        { pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir exactement 10 chiffres' }
                                    ]}
                                >
                                    <Input
                                        addonBefore={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                🇨🇮 +225
                                            </span>
                                        }
                                        maxLength={10}
                                        placeholder="Ex : 0102030405"
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Format email invalide' }]}
                                >
                                    <Input
                                        type="email"
                                        placeholder="exemple@domaine.com"
                                        prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="residence"
                                            label="Résidence"
                                        >
                                            <Input
                                                placeholder="Ex : Yopougon"
                                                prefix={<HomeOutlined style={{ color: '#bfbfbf' }} />}
                                                style={{ borderRadius: 8 }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="country"
                                            label="Pays"
                                        >
                                            <Select style={{ borderRadius: 8 }}>
                                                <Select.Option value="civ">
                                                    <span>{countryFlags.civ} Côte d'Ivoire</span>
                                                </Select.Option>
                                                <Select.Option value="fr">
                                                    <span>{countryFlags.fr} France</span>
                                                </Select.Option>
                                                <Select.Option value="us">
                                                    <span>{countryFlags.us} USA</span>
                                                </Select.Option>
                                                <Select.Option value="uk">
                                                    <span>{countryFlags.uk} UK</span>
                                                </Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        {/* Colonne droite - Sécurité */}
                        <Col xs={24} lg={12}>
                            <Card
                                className="profile-card"
                                title={
                                    <span>
                                        <LockOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                                        Sécurité
                                    </span>
                                }
                                style={{ marginBottom: 16 }}
                                headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            >
                                <Form.Item
                                    name="password"
                                    label="Nouveau mot de passe"
                                    extra="Laissez vide pour conserver le mot de passe actuel"
                                >
                                    <Input.Password
                                        placeholder="••••••••"
                                        prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="confirmPassword"
                                    label="Confirmer le mot de passe"
                                    dependencies={['password']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        placeholder="••••••••"
                                        prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <div style={{
                                    padding: 16,
                                    background: '#e6f7ff',
                                    borderRadius: 8,
                                    border: '1px solid #91d5ff',
                                    marginTop: 16
                                }}>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#0050b3', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <InfoCircleOutlined /> Conseils de sécurité
                                    </h4>
                                    <ul style={{ margin: 0, paddingLeft: 20, color: '#595959', fontSize: 13 }}>
                                        <li>Utilisez au moins 8 caractères</li>
                                        <li>Combinez lettres, chiffres et symboles</li>
                                        <li>Évitez les informations personnelles</li>
                                        <li>Ne réutilisez pas d'anciens mots de passe</li>
                                    </ul>
                                </div>
                            </Card>

                            {/* Infos supplémentaires */}
                            <Card
                                className="profile-card"
                                title={
                                    <span>
                                        <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                                        Informations du compte
                                    </span>
                                }
                                headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            >
                                <Row gutter={[16, 8]}>
                                    <Col span={12}>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>ID Utilisateur</div>
                                        <div style={{ fontWeight: 500, fontSize: 13, fontFamily: 'monospace' }}>
                                            {currentUser?._id?.slice(-8) || '—'}
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Rôle</div>
                                        <div style={{ fontWeight: 500, fontSize: 13 }}>
                                            {currentUser?.role || 'Administrateur'}
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Créé le</div>
                                        <div style={{ fontWeight: 500, fontSize: 13 }}>
                                            {currentUser?.createdAt
                                                ? new Date(currentUser.createdAt).toLocaleDateString('fr-FR')
                                                : '—'}
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Dernière connexion</div>
                                        <div style={{ fontWeight: 500, fontSize: 13 }}>
                                            {currentUser?.lastLogin
                                                ? new Date(currentUser.lastLogin).toLocaleDateString('fr-FR')
                                                : 'Aujourd\'hui'}
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>

                    {/* Bouton d'action */}
                    <Card className="profile-card" style={{ marginTop: 24 }} bodyStyle={{ padding: 16 }}>
                        <Row>
                            <Col xs={24}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: isMobile ? 'stretch' : 'flex-end',
                                    gap: 12,
                                    flexDirection: isMobile ? 'column' : 'row'
                                }}>
                                    <Button
                                        size={controlSize}
                                        onClick={() => form.resetFields()}
                                        style={{
                                            borderRadius: 8,
                                            width: isMobile ? '100%' : 'auto'
                                        }}
                                    >
                                        Réinitialiser
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={isSubmitting}
                                        icon={<SaveOutlined />}
                                        size={controlSize}
                                        style={{
                                            borderRadius: 8,
                                            background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                                            border: 'none',
                                            width: isMobile ? '100%' : 'auto',
                                            minWidth: 200
                                        }}
                                    >
                                        {isSubmitting ? 'Enregistrement...' : 'Enregistrer les modifications'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Form>
            </div>
        </div>
    );
};

export default Profile;
